import { useState, useCallback, useEffect, useRef } from 'react';
import { trpc } from '../api/trpc';
import { ConnectorDeleteControl } from './ConnectorDeleteControl';

// AWS connector settings panel — read-only summary of the tier + connected
// accounts, plus a test-connection button and a "change tier" entry that
// re-runs the install wizard. Mirrors MicrosoftConnectorSettings shape
// (info card + actions) but the data model is different: tier lives on
// Connector.config, the rich account details live on AwsConnectorAccount.
//
// Spec: docs/spec/topics/connectors/aws-provisioning.md §8.2.

interface Props {
  connector: {
    id: string;
    name: string;
    config: Record<string, unknown>;
  };
  onClose: () => void;
  /** Called when the user clicks "Change tier" — host should re-mount the
   *  install wizard. */
  onChangeTier?: () => void;
  onSaved: () => void;
}

interface AwsAccount {
  id: string;
  awsAccountId: string;
  accountAlias: string | null;
  isManagementAccount: boolean;
  regions: string[];
  status: string;
  createdAt: string;
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '6px 0',
      fontSize: 12,
      borderBottom: '1px solid var(--color-border)',
      gap: 12,
    }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{
        color: 'var(--color-text-primary)',
        fontFamily: mono ? 'var(--font-mono)' : undefined,
        fontSize: mono ? 11 : undefined,
        textAlign: 'right',
        maxWidth: '60%',
        wordBreak: 'break-all',
      }}>{value}</span>
    </div>
  );
}

const TIER_LABELS: Record<string, string> = {
  'posture-only': 'Posture only',
  'posture-and-compliance': 'Posture + Compliance',
  'full-read': 'Full read',
};

export function AwsConnectorSettings({ connector, onClose, onChangeTier, onSaved }: Props) {
  const cfg = connector.config;
  const tier = cfg['tier'] as string | undefined;
  const defaultRegion = cfg['defaultRegion'] as string | undefined;

  const accountsQuery = trpc.connectors.provisioning.aws.listAccounts.useQuery();
  const testMut = trpc.connectors.testConnection.useMutation();
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testing, setTesting] = useState(false);

  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testMut.mutateAsync({ connectorId: connector.id });
      setTestResult(result as { ok: boolean; message: string });
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  }, [connector.id, testMut]);

  // Re-run the connection test as soon as the panel opens, so a failed
  // connector shows its failure reason without the user clicking anything.
  const autoTested = useRef(false);
  useEffect(() => {
    if (autoTested.current) return;
    autoTested.current = true;
    void handleTest();
  }, [handleTest]);

  const accounts = (accountsQuery.data ?? []) as AwsAccount[];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0' }}>
      {/* Connector configuration */}
      <div style={{
        padding: 14,
        borderRadius: 8,
        background: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Configuration</div>
        {tier && <InfoRow label="Tier" value={TIER_LABELS[tier] ?? tier} />}
        {defaultRegion && <InfoRow label="Default region" value={defaultRegion} mono />}
        <InfoRow label="Auth mode" value="OIDC web identity (KMS-signed JWT)" />
      </div>

      {/* Connected accounts */}
      <div style={{
        padding: 14,
        borderRadius: 8,
        background: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
          Connected AWS accounts
          {accounts.length > 0 && (
            <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: 6 }}>
              ({accounts.length})
            </span>
          )}
        </div>
        {accountsQuery.isLoading && (
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Loading…</div>
        )}
        {!accountsQuery.isLoading && accounts.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            No AWS accounts connected yet. Use "Change tier" or re-run the install to add one.
          </div>
        )}
        {accounts.map((acct) => (
          <div key={acct.id} style={{ paddingBottom: 6 }}>
            <InfoRow
              label={acct.accountAlias ? `${acct.accountAlias}` : `Account`}
              value={acct.awsAccountId}
              mono
            />
            <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
              <span>{acct.regions.length} region{acct.regions.length === 1 ? '' : 's'}</span>
              {acct.isManagementAccount && <span>· Organizations management</span>}
              <span>· Status: {acct.status}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Test result */}
      {testResult && (
        <div style={{
          padding: 10,
          borderRadius: 6,
          fontSize: 12,
          background: testResult.ok ? 'rgba(0,180,0,0.08)' : 'rgba(220,0,0,0.08)',
          border: `1px solid ${testResult.ok ? 'var(--sev-success)' : 'var(--sev-critical)'}`,
        }}>
          {testResult.message}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => { void handleTest(); }}
          disabled={testing}
          style={{ flex: 1, minWidth: 140 }}
        >
          {testing ? 'Testing…' : 'Test connection'}
        </button>
        {onChangeTier && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onChangeTier}
            style={{ flex: 1, minWidth: 140 }}
          >
            Change tier…
          </button>
        )}
        <button type="button" className="btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>

      {onChangeTier && (
        <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: 0 }}>
          Changing the tier re-launches the install wizard. You'll need to update the
          CloudFormation stack in your AWS account with the new template.
        </p>
      )}

      <ConnectorDeleteControl
        connectorId={connector.id}
        connectorLabel={connector.name}
        onDeleted={onSaved}
      />
    </div>
  );
}
